import { Box } from '@chakra-ui/react'
import Head from 'next/head'

import assertDefined from '@/utils/assertDefined'

import { useAppContext } from '@/client/context/app'
import { useUser } from '@/client/context/user'

import Layout from '@/components/Layout/Layout'
import RestrictedPageAccess from '@/components/Layout/RestrictedPage/RestrictedPageAccess'
import RestrictedPagePotentiallySuspended from '@/components/Layout/RestrictedPage/RestrictedPagePotentiallySuspended'
import RestrictedPageRejected from '@/components/Layout/RestrictedPage/RestrictedPageRejected'
import RestrictedPageSuspended from '@/components/Layout/RestrictedPage/RestrictedPageSuspended'
import RestrictedPageVerifyEmail from '@/components/Layout/RestrictedPage/RestrictedPageVerifyEmail'

import { PAGE_ACCESS_ERROR_TO_METADATA } from './constants'
import getPageAccessError from './getPageAccessError'
import type { PageAccessError, PageAccessOptions } from './types'

// oxlint-disable-next-line @typescript-eslint/ban-types
function TransformedPageInternal<T extends {}>({
  Page,
  options,
  props,
}: {
  Page: React.FC<T>
  options: PageAccessOptions
  props: T
}): React.ReactElement {
  const user = useUser()
  const appContext = useAppContext()

  const pageAccessError = getPageAccessError({ user, options })

  if (pageAccessError == null) {
    appContext.setStatusCode(200)

    return (
      <Box className={options.disableAutocapture === true ? 'ph-no-capture' : undefined}>
        <Page {...props} />
      </Box>
    )
  }

  // page access disallowed

  const DISALLOWED_REASON_TO_COMPONENT: Record<PageAccessError, () => JSX.Element> = {
    not_logged_in: () => (
      <RestrictedPageAccess
        title="Please sign in to access this page"
        description="You need to be logged in to your account to access this page."
      />
    ),
    email_not_verified: RestrictedPageVerifyEmail,
    not_enough_permission: () => (
      <RestrictedPageAccess
        title="Access Denied"
        description="You do not have the permission to access this page."
        hideLoginButton
      />
    ),
    onboarding_already_finished: () => (
      <RestrictedPageAccess
        title="Access Denied"
        description="You have already finished onboarding! Please return to the homepage."
        hideLoginButton
      />
    ),
    onboarding_not_finished: () => (
      <RestrictedPageAccess
        title="Almost There"
        description="You still need to finish your onboarding."
        hideLoginButton
        hideHomeButton
        showOnboardingButton
      />
    ),
    user_rejected: RestrictedPageRejected,
    user_suspended: () => {
      assertDefined(user.suspensionAction, 'suspensionAction as expected but null')
      return <RestrictedPageSuspended suspensionAction={user.suspensionAction} />
    },
    user_maybe_suspended: () => {
      assertDefined(user.suspensionAction, 'suspensionAction as expected but null')
      return <RestrictedPagePotentiallySuspended suspensionAction={user.suspensionAction} />
    },
    not_enough_age: () => (
      <RestrictedPageAccess
        title="You are too young to access this page!"
        description="Please come back after you turn 13 years old."
        hideLoginButton
      />
    ),
  }

  const metadata = PAGE_ACCESS_ERROR_TO_METADATA[pageAccessError]
  const children = DISALLOWED_REASON_TO_COMPONENT[pageAccessError]()

  appContext.setStatusCode(403)

  return (
    <Layout title={`${metadata.title}`}>
      <Head>
        <meta name="robots" content="noindex" />
      </Head>
      {children}
    </Layout>
  )
}

// oxlint-disable-next-line @typescript-eslint/ban-types
export default function makePage<T extends {}>(
  Page: React.FC<T>,
  options: PageAccessOptions = {}
): React.FC<T> {
  const TransformedPage = (props: T) => (
    <TransformedPageInternal<T> Page={Page} options={options} props={props} />
  )
  return TransformedPage
}
